import type { OrderSettings, OrderSettingsDropdown } from '@/api/models/OrderSettings';
import { OrdersPageType } from '@/components/Orders/Dashboard/OrdersPresentation';

export const orderSettingsKeys: Record<OrdersPageType, keyof OrderSettings> = {
  [OrdersPageType.Clubs]: 'clubs',
  [OrdersPageType.Components]: 'components',
  [OrdersPageType.Putters]: 'putter',
};

const normalizeName = (name: string) => name.replace(/\s/g, '').toLowerCase();

export const getOrderNotificationTypeIds = (settings: OrderSettings | null | undefined): number[] => {
  const ids = [settings?.clubs?.notificationTypeId, settings?.components?.notificationTypeId, settings?.putter?.notificationTypeId];
  return [...new Set(ids.filter((id): id is number => Boolean(id && id > 0)))];
};

// Labels establish the design order only. IDs always come from the cached API lookup.
const eventLabels = ['Saved Order', 'Submitted Order', 'Completed Order', 'Cancelled Order', 'Deleted Order', 'Build Order', 'Printed Order', 'Completed Partial Order', 'Partial Order'];

export const getOrderNotificationOptions = (options: OrderSettingsDropdown[]): OrderSettingsDropdown[] => {
  const ordered = eventLabels.map((label) => options.find((option) => option.isActive && normalizeName(option.name) === normalizeName(label)));
  if (ordered.some((option) => !option) || new Set(ordered.map((option) => option?.id)).size !== eventLabels.length) {
    throw new Error('Email notification options are incomplete. Please retry when online.');
  }
  return ordered.filter((option): option is OrderSettingsDropdown => Boolean(option));
};

export type OrderRefreshRate = 1 | 5 | 10;

export const getOrderRefreshRate = (setting: string | null | undefined): OrderRefreshRate => {
  const rate = parseSettingsObject(setting).mainscreenRefreshRate;
  return rate === 1 || rate === 10 ? rate : 5;
};

export interface OrderSearchDefaults {
  headTypes: number[];
  buildLocations: number[];
  shipFromLocations: number[];
  shipToLocations: number[];
  orderStatuses: number[];
  orderAccounts: number[];
  appUsers: number[];
}

export const orderSearchDefaultFields: Record<OrdersPageType, (keyof OrderSearchDefaults)[]> = {
  [OrdersPageType.Clubs]: ['headTypes', 'buildLocations', 'orderStatuses', 'orderAccounts', 'appUsers'],
  [OrdersPageType.Components]: ['headTypes', 'shipFromLocations', 'shipToLocations', 'orderStatuses', 'orderAccounts', 'appUsers'],
  [OrdersPageType.Putters]: ['buildLocations', 'orderStatuses', 'orderAccounts', 'appUsers'],
};

const parseSettingsObject = (setting: string | null | undefined): Record<string, unknown> => {
  if (!setting?.trim()) return {};
  try {
    const parsed: unknown = JSON.parse(setting);
    if (parsed === null) return {};
    if (typeof parsed === 'object' && !Array.isArray(parsed)) return parsed as Record<string, unknown>;
  } catch {
    // Invalid settings must not be replaced with empty defaults on save.
  }
  throw new Error('Your search defaults could not be read. Please reopen Settings after signing in again.');
};

export const parseOrderSearchDefaults = (setting: string | null | undefined): OrderSearchDefaults => {
  const source = parseSettingsObject(setting);
  const readIds = (field: keyof OrderSearchDefaults): number[] => {
    const value = source[field];
    if (value === undefined || value === null) return [];
    if (!Array.isArray(value) || value.some((id) => typeof id !== 'number' || !Number.isSafeInteger(id) || id <= 0)) {
      throw new Error('Your search defaults contain invalid selections. Please sign in again.');
    }
    return [...new Set(value)];
  };
  return {
    headTypes: readIds('headTypes'),
    buildLocations: readIds('buildLocations'),
    shipFromLocations: readIds('shipFromLocations'),
    shipToLocations: readIds('shipToLocations'),
    orderStatuses: readIds('orderStatuses'),
    orderAccounts: readIds('orderAccounts'),
    appUsers: readIds('appUsers'),
  };
};

export const serializeOrderSearchDefaults = (setting: string, page: OrdersPageType, defaults: OrderSearchDefaults, refreshRate?: OrderRefreshRate): string => {
  const original = parseOrderSearchDefaults(setting);
  const fields = orderSearchDefaultFields[page];
  const changed = fields.some((field) => JSON.stringify(original[field]) !== JSON.stringify(defaults[field]));
  if (!changed && (refreshRate === undefined || refreshRate === getOrderRefreshRate(setting))) return setting;
  const merged = parseSettingsObject(setting);
  if (refreshRate !== undefined) merged.mainscreenRefreshRate = refreshRate;
  for (const field of fields) merged[field] = [...new Set(defaults[field])];
  return JSON.stringify(merged);
};
